import React, {
  createContext,
  useContext,
  useState,
  useLayoutEffect,
  useEffect
} from 'react';
import {
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
  CognitoRefreshToken
} from 'amazon-cognito-identity-js';

const REFRESH_TIMEOUT = 1000 * 60 * 29;

const poolData = {
  UserPoolId: process.env.REACT_APP_COGNITO_USERPOOL as string,
  ClientId: process.env.REACT_APP_COGNITO_CLIENT as string
};

const userPool = new CognitoUserPool(poolData);

type AuthContextType = {
  loading: boolean;
  authUser: CognitoUser | null;
  setAuthUser: React.Dispatch<React.SetStateAction<CognitoUser | null>>;
  session: CognitoUserSession | null;
  setSession: React.Dispatch<React.SetStateAction<CognitoUserSession | null>>;
};

const AuthUserContext = createContext<AuthContextType>({
  loading: true,
  authUser: null,
  setAuthUser: () => null,
  session: null,
  setSession: () => null
});

const getRefreshSessionPromise = (
  user: CognitoUser,
  refreshToken: CognitoRefreshToken
) =>
  new Promise<CognitoUserSession>((resolve, reject) => {
    user.refreshSession(
      refreshToken,
      (err: any, session: CognitoUserSession) => {
        if (err) {
          reject(err);
        }
        resolve(session);
      }
    );
  });

const getUserFromLocalStorage = async (): Promise<{
  user: CognitoUser;
  session: CognitoUserSession;
} | null> => {
  const currentUser = userPool.getCurrentUser();

  console.log('get user from local storage');

  if (currentUser) {
    const session: CognitoUserSession = await new Promise(resolve =>
      currentUser.getSession((err: any, session: CognitoUserSession) => {
        if (err || !session) {
          if (err) console.log('error get session: ', err);
          if (!session)
            console.log('error session is null in getsession', session);
          return resolve(undefined);
        }

        console.log('get session no error: ', session);
        return resolve(session);
      })
    );

    if (!session) {
      console.log('no session');
      // if (session && !session.isValid())
      // console.log('invalid session: ', session);
      return null;
    }

    console.log('session retrieved: ', session);

    // refresh current session :)
    const refreshPromise = getRefreshSessionPromise(
      currentUser,
      session.getRefreshToken()
    );

    try {
      console.log('refresh session');
      const refreshedSession = await refreshPromise;
      if (refreshedSession.isValid()) {
        console.log('success refreshing session: ', refreshedSession);
        return { user: currentUser, session: refreshedSession };
      }
      console.log('refreshed session not valid: ', refreshedSession);
      return null;
    } catch (e) {
      console.log('error refreshing session: ', e);
      return null;
    }
  }

  console.log('no user');
  return null;
};

const AuthProvider: React.FC = ({ children }) => {
  const [authUser, setAuthUser] = useState<CognitoUser | null>(null);
  const [session, setSession] = useState<CognitoUserSession | null>(null);

  const [loading, setLoading] = useState(true);

  useLayoutEffect(() => {
    const getInitialUser = async () => {
      setLoading(true);
      const localStorageUser = await getUserFromLocalStorage();
      if (!localStorageUser) {
        setAuthUser(null);
        setSession(null);
        setLoading(false);
        return;
      }
      const { user: initialUser, session: initialSession } = localStorageUser;
      setAuthUser(initialUser);
      setSession(initialSession);
      setLoading(false);
    };

    getInitialUser();
  }, []);

  useEffect(() => {
    const refreshInterval = () =>
      setInterval(async () => {
        if (authUser) {
          const session: CognitoUserSession = authUser.getSession(
            (err: any, session: CognitoUserSession) => {
              if (err || !session) {
                return null;
              }
              return session;
            }
          );

          const refreshPromise = getRefreshSessionPromise(
            authUser,
            session.getRefreshToken()
          );

          try {
            const newSession = await refreshPromise;
            setSession(newSession);
          } catch (err) {
            console.log('err auto refreshing session: ', err);
          }
        }
      }, REFRESH_TIMEOUT);
    const interval = refreshInterval();
    return () => clearInterval(interval);
  }, [authUser]);

  return (
    <AuthUserContext.Provider
      value={{ loading, authUser, setAuthUser, session, setSession }}
    >
      {children}
    </AuthUserContext.Provider>
  );
};

const useAuthContext = () => useContext(AuthUserContext);

export { AuthProvider, useAuthContext, userPool };
export default AuthProvider;
